import React, { useState } from "react";
import { Button, Field, NumberField, PanelSection } from "./StudioControls";
import { bounds, distributionUnits } from "./studioModel";
import { ALIGN_TARGETS, ORIGINS, RESIZE_MODES, alignSelection, mixedValue, moveSelection, parseEntry, resizeSelection } from "./studioTransform";

const FIELDS = [["x", "X"], ["y", "Y"], ["w", "Width"], ["h", "Height"], ["rotation", "Rotation °"], ["opacity", "Opacity %"]];
const ORIGIN_LABELS = { tl: "Top left", t: "Top", tr: "Top right", l: "Left", c: "Center", r: "Right", bl: "Bottom left", b: "Bottom", br: "Bottom right" };

// Relative entry applies once, on Enter or blur, then resets, so a
// half-typed value is never applied twice.
function RelativeField({ label, onApply }) {
  const [value, setValue] = useState("");
  const apply = () => {
    const text = value.trim();
    if (text) {
      const entry = parseEntry(/^[+*×x/]|^-=|%$/.test(text) ? text : `+${text}`);
      if (entry) onApply(entry);
    }
    setValue("");
  };
  return (
    <Field label={label}>
      <input type="text" inputMode="decimal" placeholder="±0" value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={apply}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } if (e.key === "Escape") setValue(""); }} />
    </Field>
  );
}

export default function StudioTransformPanel({ doc, page, nodes, selected, commitNodes, distribute, keyId }) {
  const [relative, setRelative] = useState(false);
  const [mode, setMode] = useState(null);
  const [origin, setOrigin] = useState("tl");
  const [target, setTarget] = useState("selection");
  const many = nodes.length > 1;
  const resizeMode = mode || (many ? "each" : "frame");
  const headings = new Map(doc.pages.flatMap((p) => p.elements).map((n) => [n.id, n]));
  const box = bounds(nodes);
  const shown = (key) => {
    if (many && ["x", "y"].includes(key)) return { value: box[key], mixed: false };
    const m = mixedValue(nodes.map((n) => n[key]));
    return key === "opacity" && m.value !== undefined ? { ...m, value: m.value * 100 } : m;
  };
  const apply = (key, entry) => {
    const sameType = nodes.every((n) => n.type === nodes[0].type);
    commitNodes(["w", "h"].includes(key)
      ? resizeSelection(doc, nodes, { key, entry, mode: many && !sameType && resizeMode === "each" ? "frame" : resizeMode, origin, headings })
      : moveSelection(nodes, { key, entry }));
  };
  return (
    <PanelSection title="Transform" aside={<span className="ms-kicker">POINTS</span>}>
      <div className="ms-segmented" role="group" aria-label="Entry mode">
        <Button active={!relative} aria-pressed={!relative} onClick={() => setRelative(false)}>Absolute</Button>
        <Button active={relative} aria-pressed={relative} onClick={() => setRelative(true)}>Relative</Button>
      </div>
      <div className="ms-fields-two">
        {FIELDS.map(([key, label]) => {
          if (relative) return <RelativeField key={key} label={`${label} change`} onApply={(entry) => apply(key, entry)} />;
          const { value, mixed } = shown(key);
          return (
            <NumberField key={key} label={label} value={value} mixed={mixed}
              min={["w", "h"].includes(key) ? 1 : key === "opacity" ? 0 : -9999} max={key === "opacity" ? 100 : 9999}
              onChange={(v) => apply(key, { relative: false, value: v, apply: () => v })} />
          );
        })}
      </div>
      {relative && <p className="ms-hint">Type +4, -=2, *1.5 or 90% and press Enter. Each element changes by that amount.</p>}
      <Field label="Width & height">
        <select aria-label="Resize mode" value={resizeMode} onChange={(e) => setMode(e.target.value)}>
          {RESIZE_MODES.filter(([m]) => many || m === "frame" || m === "content").map(([m, label]) => <option key={m} value={m}>{label}</option>)}
        </select>
      </Field>
      <div className="ms-origin" role="radiogroup" aria-label="Transform origin">
        {ORIGINS.map((o) => <button key={o} type="button" role="radio" aria-checked={origin === o} aria-label={ORIGIN_LABELS[o]} title={`Origin: ${ORIGIN_LABELS[o]}`}
          className={origin === o ? "is-active" : ""} onClick={() => setOrigin(o)} />)}
      </div>
      <p className="ms-hint">{{
        frame: "Changes the frame only; text re-wraps and keeps its size.",
        content: "Scales the frame and its type together.",
        group: "Scales the selection as one object around the origin.",
        each: "Scales every element by the same factor around its own origin. Heading ornaments grow away from the text.",
      }[resizeMode]}</p>
      <Field label="Align to">
        <select aria-label="Align to" value={target} onChange={(e) => setTarget(e.target.value)}>
          {ALIGN_TARGETS.map(([t, label]) => <option key={t} value={t} disabled={t === "key" && !many}>{label}{t === "key" && many ? ` (${nodes.find((n) => n.id === keyId)?.name?.slice(0, 18) || "first selected"})` : ""}</option>)}
        </select>
      </Field>
      <div className="ms-button-row">
        {["left", "center", "right", "top", "middle", "bottom"].map((direction) => (
          <Button key={direction} icon={direction} title={`Align ${direction}`} onClick={() => commitNodes(alignSelection(page, nodes, direction, target, keyId))} />
        ))}
      </div>
      <div className="ms-fields-two">
        <Button disabled={distributionUnits(page, selected).length < 3} onClick={() => distribute("x")}>Space evenly ↔</Button>
        <Button disabled={distributionUnits(page, selected).length < 3} onClick={() => distribute("y")}>Space evenly ↕</Button>
      </div>
    </PanelSection>
  );
}
